import React, { useEffect, useState } from 'react';
import { useHistory } from "react-router-dom";
import { Fetcher } from '../../../Helpers/fetcher';
import { Notify } from '../../../Helpers/notification';
import { Card, Form, Button, InputGroup, Alert } from 'react-bootstrap';
import "./index.css"
import { buildAclPayload, buildModuleListingPayload, hasAnyPermissionSelected, hasRequiredModuleSelections, MultiSelectModules, sortModules } from '../helpers/aclHelper';
import AclTable from '../components/AclTable';
const CreateAcl = ({ app }) => {
    const apiPath = app.apiPath;
    const userId = app.userId
    const history = useHistory();

    const [aclName, setAclName] = useState('');
    const [moduleList, setModuleList] = useState([]);
    const [permissions, setPermissions] = useState({});
    const [search, setSearch] = useState('');
    const [expandedModules, setExpandedModules] = useState({});
    const [loading, setLoading] = useState(false);
    const [moduleListingData, setModuleListingData] = useState({});
    const [multiSelectOptions, setMultiSelectOptions] = useState({});

    const fetchModuleData = async () => {
        try {
            const cliUrl = `${apiPath}/api/obd/cli/${userId}`;
            const cliApiUrl = `${apiPath}/api/obd/cli/api/${userId}`;
            const groupUrl = `${apiPath}/api/obd/groups`;
            const locationUrl = `${apiPath}/api/obd/locations`;
            const dialerUrl = `${apiPath}/api/obd/dialers`;

            const transform = (arr, label, value) =>
                (Array.isArray(arr) ? arr : []).map(item => ({
                    name: item?.[label] ?? "",
                    id: item?.[value] ?? "",
                    dialerName: item?.dialerName ?? ""
                }));

            const [cliRes, cliApiRes, groupRes, locationRes, dialerRes] = await Promise.all([
                Fetcher.get(cliUrl),
                Fetcher.get(cliApiUrl),
                Fetcher.get(groupUrl),
                Fetcher.get(locationUrl),
                Fetcher.get(dialerUrl)
            ]);

            setMultiSelectOptions({
                "Manage CLI": transform(cliRes, "cli", "cliId"),
                "Manage API CLI": transform(cliApiRes, "cli", "cliId"),
                "Manage Groups": transform(groupRes, "groupName", "groupId"),
                "Manage Locations": transform(locationRes, "locationName", "locationId"),
                "Manage Dialers": transform(dialerRes, "dialerName", "dialerId")
            });

        } catch (err) {
            console.log("fetchModuleData error", err);
            Notify.notify("Error loading dropdown data");
        }
    };

    const getModuleList = async () => {
        setLoading(true);
        try {
            const res = await Fetcher.get(`${apiPath}/api/obd/acl/module/list`);

            const data = (Array.isArray(res) ? res : []).map(m => {
                let children = [];

                try {
                    children = Array.isArray(m.childModule)
                        ? m.childModule
                        : JSON.parse(m.childModule || "[]");
                } catch {
                    children = [];
                }

                return {
                    ...m,
                    children
                };
            });

            setModuleList(data);

            const init = {};

            data.forEach(m => {
                const hasChild = m.children?.length > 0;

                init[m.mname] = hasChild
                    ? { read: false, view_permission: false }
                    : {
                        read: false,
                        add: false,
                        edit: false,
                        delete: false,
                        view_permission: false
                    };

                // Children
                if (hasChild) {
                    m.children.forEach(c => {
                        init[c.moduleName] = {
                            read: false,
                            add: false,
                            edit: false,
                            delete: false,
                            view_permission: false
                        };
                    });
                }
            });

            setPermissions(init);

        } catch {
            Notify.notify("Error loading modules");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchModuleData();
        getModuleList();
    }, []);

    const togglePermission = (id, key) => {
        setPermissions(prev => {
            const updated = {
                ...prev,
                [id]: {
                    ...(prev[id] || {}),
                    [key]: !prev?.[id]?.[key]
                }
            };

            if (
                key === "view_permission" &&
                updated[id]?.view_permission === false
            ) {
                setModuleListingData(prev => {
                    const copy = { ...prev };
                    delete copy[id];
                    return copy;
                });
            }

            return updated;
        });
    };

    const toggleAll = (id, keys, checked) => {
        setPermissions(prev => {
            const updated = { ...prev };
            if (!updated[id]) updated[id] = {};

            keys.forEach(k => {
                updated[id][k] = checked;
            });

            return updated;
        });
    };

    const isAllChecked = (id, keys) => {
        const p = permissions[id] || {};
        return keys.every(k => p[k] === true);
    };

    const toggleExpand = (id) => {
        setExpandedModules(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const filteredModules = moduleList.sort(sortModules).filter(m =>
        m.mname.toLowerCase().includes(search.toLowerCase())
    );

    const handleSubmit = () => {
        if (!aclName.trim()) {
            Notify.notify("ACL Name is required");
            return;
        }

        if (!hasAnyPermissionSelected(moduleList, permissions)) {
            Notify.notify("Please select at least one permission before updating");
            return;
        }

        if (!hasRequiredModuleSelections(moduleListingData, permissions, MultiSelectModules)) {
            Notify.notify("Please select required items for enabled modules");
            return;
        }

        const cleanedModuleListingData = buildModuleListingPayload(moduleListingData)
        const payload = {
            ...buildAclPayload({
                moduleList,
                permissions,
                aclName,
                userId,
            }),
            moduleListingData: JSON.stringify(cleanedModuleListingData)
        };

        Fetcher.post(`${apiPath}/api/obd/add/acl/settings`, {
            method: "POST",
            headers: { "Content-type": "application/json" },
            body: JSON.stringify(payload)
        })
            .then(res => {
                if (res.message === 'Parameters Incorrect') {
                    Notify.notify('Parameters Incorrect');
                } else {
                    Notify.notify(res.message);
                    history.push("/acl-settings");
                }
            })
            .catch(err => {
                console.log('Error in Adding Acl', err);
                Notify.notify('Error in Adding Acl');
            });
    };
    return (
        <Card>
            <Card.Header>Create ACL</Card.Header>
            <Card.Body>

                <Form.Group className="mb-3">
                    <Form.Label>ACL Name</Form.Label>
                    <Form.Control
                        value={aclName}
                        onChange={(e) => setAclName(e.target.value)}
                        placeholder="Enter ACL name"
                    />
                </Form.Group>

                <Alert variant='warning'>Note: Only modules with View Permission enabled will be visible in the user sidebar.</Alert>

                <InputGroup className="mb-3">
                    <Form.Control
                        placeholder="Search module name..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </InputGroup>

                <AclTable
                    loading={loading}
                    filteredModules={filteredModules}
                    permissions={permissions}
                    expandedModules={expandedModules}
                    moduleListingData={moduleListingData}
                    multiSelectOptions={multiSelectOptions}
                    togglePermission={togglePermission}
                    toggleAll={toggleAll}
                    isAllChecked={isAllChecked}
                    toggleExpand={toggleExpand}
                    setModuleListingData={setModuleListingData}
                />

                <Button onClick={handleSubmit}>
                    Save ACL
                </Button>

            </Card.Body>
        </Card>
    );
};

export default CreateAcl;